import { blackScholesCall } from "./blackScholes";
import { generateStrikes } from "./strikeIntervals";
import {
  getNextExpirationDates,
  formatExpirationDate,
} from "./expirationDates";
import {
  getTradingDaysUntilExpiration,
  getDaysToExpiration,
  daysToYears,
} from "./tradingDays";
import dayjs from "dayjs";

/**
 * Generate scenarios (1% to 15% price movements)
 * @param {number} stockPrice - Current stock price
 * @param {number} strike - Strike price
 * @param {number} timeToExpiration - Time to expiration in years (for future price calc)
 * @param {number} futureIV - Implied volatility (as decimal) for future price calculation
 * @param {number} riskFreeRate - Risk-free rate (as decimal)
 * @param {number} currentOptionPrice - Current option theoretical price
 * @returns {object} - Scenarios object
 */
function generateScenarios(
  stockPrice,
  strike,
  timeToExpiration,
  futureIV,
  riskFreeRate,
  currentOptionPrice
) {
  const scenarios = {};

  // Generate scenarios from 1% to 15%
  for (let movePercent = 1; movePercent <= 15; movePercent++) {
    const futureStockPrice = stockPrice * (1 + movePercent / 100);

    // Calculate future option price with futureIV (not entry IV)
    const theoreticalPriceFuture = blackScholesCall(
      futureStockPrice,
      strike,
      timeToExpiration,
      riskFreeRate,
      futureIV
    );

    const priceChange = theoreticalPriceFuture - currentOptionPrice;
    const priceChangePct =
      currentOptionPrice > 0 ? (priceChange / currentOptionPrice) * 100 : 0;

    scenarios[`${movePercent}%`] = {
      future_stock_price: Math.round(futureStockPrice * 100) / 100,
      theoretical_price_future:
        Math.round(theoreticalPriceFuture * 10000) / 10000,
      price_change: Math.round(priceChange * 10000) / 10000,
      price_change_pct: Math.round(priceChangePct * 100) / 100,
    };
  }

  return scenarios;
}

/**
 * Generate trading days data for a strike
 * @param {number} stockPrice - Current stock price
 * @param {number} strike - Strike price
 * @param {dayjs.Dayjs} expirationDate - Expiration date
 * @param {number} iv - Implied volatility (for entry price)
 * @param {number} futureIV - Implied volatility (for future scenarios)
 * @param {number} riskFreeRate - Risk-free rate
 * @param {dayjs.Dayjs} customDate - Custom "today" date (optional, defaults to current date)
 * @returns {array} - Trading days array
 */
function generateTradingDaysForStrike(
  stockPrice,
  strike,
  expirationDate,
  iv,
  futureIV,
  riskFreeRate,
  customDate = null
) {
  const today = customDate || dayjs();
  const tradingDays = getTradingDaysUntilExpiration(expirationDate, customDate);
  const tradingDaysData = [];

  // Calculate entry price ONCE - from today to expiration (same for all trading days)
  const daysFromTodayToExpiration = getDaysToExpiration(today, expirationDate);
  const timeFromTodayToExpiration = daysToYears(daysFromTodayToExpiration);
  const theoreticalPriceNow = blackScholesCall(
    stockPrice,
    strike,
    timeFromTodayToExpiration,
    riskFreeRate,
    iv
  );

  tradingDays.forEach((tradingDay) => {
    const daysToExpiration = getDaysToExpiration(tradingDay, expirationDate);

    // Use minimum of 1 day (0.0027 years) like Python does
    const timeToExpiration = Math.max(daysToYears(daysToExpiration), 0.0027);

    // Calculate calendar days passed since today
    const tradingDaysPassed = tradingDay.diff(today, "day");

    // Generate scenarios for this trading day
    const scenarios = generateScenarios(
      stockPrice,
      strike,
      timeToExpiration,
      futureIV,
      riskFreeRate,
      theoreticalPriceNow
    );

    tradingDaysData.push({
      trading_day: tradingDay.format("YYYY-MM-DD"),
      day_of_week: tradingDay.format("dddd"),
      trading_days_passed: tradingDaysPassed,
      days_to_expiration: daysToExpiration,
      current_stock_price: Math.round(stockPrice * 100) / 100,
      theoretical_price_now: Math.round(theoreticalPriceNow * 10000) / 10000,
      scenarios,
    });
  });

  return tradingDaysData;
}

/**
 * Generate complete options data structure for manual mode
 * @param {object} params - Input parameters
 * @param {number} params.stockPrice - Current stock price
 * @param {number} params.iv - Implied volatility (as percentage, e.g., 50 for 50%)
 * @param {number} params.futureIV - Future implied volatility (as percentage, optional, defaults to iv)
 * @param {number} params.riskFreeRate - Risk-free rate (as percentage, e.g., 4.5 for 4.5%)
 * @param {number} params.numExpirations - Number of expirations (default 3)
 * @param {number} params.strikesAbove - Strikes above current price (default 10)
 * @param {number} params.strikesBelow - Strikes below current price (default 5)
 * @param {dayjs.Dayjs} params.customDate - Custom "today" date (optional)
 * @returns {object} - Complete options data structure matching API format
 */
export function generateManualOptionsData({
  stockPrice,
  iv,
  futureIV = null,
  riskFreeRate = 4.5,
  numExpirations = 3,
  strikesAbove = 10,
  strikesBelow = 5,
  customDate = null,
}) {
  // Convert percentages to decimals
  const ivDecimal = iv / 100;
  const futureIVDecimal = (futureIV !== null ? futureIV : iv) / 100;
  const riskFreeRateDecimal = riskFreeRate / 100;

  // Generate expiration dates (next N Fridays)
  const expirationDates = getNextExpirationDates(numExpirations, customDate);

  // Generate strike prices
  const strikes = generateStrikes(stockPrice, strikesBelow, strikesAbove);

  const optionsData = {};

  // For each expiration
  expirationDates.forEach((expirationDate) => {
    const expirationKey = formatExpirationDate(expirationDate);
    const tradingDays = getTradingDaysUntilExpiration(expirationDate, customDate);

    const strikesData = strikes.map((strike) => ({
      strike,
      trading_days: generateTradingDaysForStrike(
        stockPrice,
        strike,
        expirationDate,
        ivDecimal,
        futureIVDecimal,
        riskFreeRateDecimal,
        customDate
      ),
    }));

    optionsData[expirationKey] = {
      expiration_date: expirationKey,
      trading_days_count: tradingDays.length,
      strikes: strikesData,
    };
  });

  return optionsData;
}
